/**
 * Which Excel export the Download button uses: the shared mapping (`renderAurigaPage`) or the legacy
 * AG-Grid export.
 *
 * The code flag `enableSharedExcelExport` sets the path for everyone. A dev-team admin can override it for
 * their own browser from the Admin panel, to compare the two exports; the override is stored in
 * localStorage, like Preview Mode. Choosing the same path as the flag clears the override, so the browser
 * follows the flag again (and keeps following it if the flag later changes).
 *
 * Components use `useSharedExcelExport` (`@hooks/useSharedExcelExport`), which reads the flag from
 * `AurigaContext`; these functions are the storage underneath it.
 */

/** localStorage key holding a per-browser override: 'true' (shared) or 'false' (legacy). Absent: follow the flag. */
export const SHARED_EXCEL_EXPORT_KEY = 'auriga-shared-excel-export';

/**
 * Read this browser's override.
 *
 * @returns `true` (shared export) or `false` (legacy export) when an admin has overridden the flag here;
 *   `null` when the browser follows the code flag. Any other stored value counts as no override.
 */
export const getSharedExcelExportOverride = (): boolean | null => {
  const stored = localStorage.getItem(SHARED_EXCEL_EXPORT_KEY);
  if (stored === 'true') return true;
  if (stored === 'false') return false;
  return null;
};

/**
 * Record an admin's choice for this browser.
 *
 * @param useShared - The path the admin chose: `true` for the shared export, `false` for the legacy export.
 * @param flag - The code flag `enableSharedExcelExport`. A choice equal to it clears the override, so the
 *   browser follows the flag again; a different choice is stored as the override.
 * @returns The override now stored: the choice, or `null` when it matched the flag and was cleared.
 */
export const setSharedExcelExportChoice = (useShared: boolean, flag: boolean): boolean | null => {
  if (useShared === flag) {
    localStorage.removeItem(SHARED_EXCEL_EXPORT_KEY);
    return null;
  }
  localStorage.setItem(SHARED_EXCEL_EXPORT_KEY, String(useShared));
  return useShared;
};
